import { useProductManagementErrors } from './useProductManagementErrors.ts';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

const ERRORS_LIMIT = 200;

const GRID_COLUMNS = 'minmax(200px, 280px) 1fr';

type ProductManagementErrorsPanelProps = {
  requestId: string;
};

export const ProductManagementErrorsPanel = ({ requestId }: ProductManagementErrorsPanelProps) => {
  const { data, isLoading } = useProductManagementErrors(requestId);
  const items = data?.items ?? [];

  return (
    <Box>
      {data?.hasMoreErrors && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {`Showing the first ${ERRORS_LIMIT} errors. Download the error file to see all errors.`}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          columnGap: '1.5rem',
          py: '0.5rem',
          borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
        }}
      >
        <Typography variant="tableSecondary" color="text.secondary">
          Product Identifier
        </Typography>
        <Typography variant="tableSecondary" color="text.secondary">
          Error Message
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ py: '0.75rem' }} data-testid="product-management-errors-loading">
          <Skeleton height={24} />
          <Skeleton height={24} />
        </Box>
      ) : (
        items.map((row) => (
          <Box
            key={`${row.productIdentifier}-${row.errorMessage}`}
            data-testid="product-management-error-row"
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              columnGap: '1.5rem',
              py: '0.75rem',
              borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Typography
              variant="tablePrimary"
              color="text.primary"
              sx={{ wordBreak: 'break-word' }}
            >
              {row.productIdentifier || '-'}
            </Typography>
            <Typography
              variant="tablePrimary"
              color="text.primary"
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              {row.errorMessage || '-'}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
};
